import { showDialog } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { clubIsEmptySelector } from "@/redux/user/userSlice";
import { noteClubImportIntent } from "@repo/shared/redux";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

type Args = {
  /** Mixpanel section/subsection for the prompt that follows. */
  section: string;
  subsection: string;
};

/**
 * Guards actions that only make sense with club players in place.
 *
 * Callers wrap the action in `guardClubAction`; an empty club then explains
 * itself with an import CTA instead of failing silently or firing an error
 * alert. The dialog itself lives in ModalControllerV2, so no caller has to
 * render anything.
 */
export const useEmptyClubGuard = ({ section, subsection }: Args) => {
  const dispatch = useDispatch<AppDispatch>();
  const clubIsEmpty = useSelector(clubIsEmptySelector);

  // Every user can fix an empty club — the import is free — so emptiness alone
  // decides. Callers still branch on this rather than on the selector directly,
  // since it also names what the prompt will be.
  const shouldPrompt = clubIsEmpty;

  const promptEmptyClub = useCallback(() => {
    // The dialog's only action is an import, so raising it is already the
    // signup intent — recorded here rather than on the button so it survives
    // the user reaching login by some other route out of the same dialog.
    noteClubImportIntent({ section, subsection });
    return dispatch(
      showDialog({
        modalType: "empty-club",
        data: { section, subsection },
      }),
    );
  }, [dispatch, section, subsection]);

  /** Runs `action` when the club has players, prompts for an import when not. */
  const guardClubAction = useCallback(
    (action: () => void) => {
      if (shouldPrompt) {
        promptEmptyClub();
        return;
      }
      action();
    },
    [shouldPrompt, promptEmptyClub],
  );

  return {
    shouldPromptEmptyClub: shouldPrompt,
    guardClubAction,
    promptEmptyClub,
  };
};
